import { HttpParams } from '@angular/common/http'
import { Component, DestroyRef, OnDestroy, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { DataMap } from '@grapecity/wijmo.grid'

import {
    BaseComponent,
    EquipmentReport,
    EquipmentReportTypeEx,
    FeatureType,
    Grid,
    IModalEditOptions,
    ListItem,
    ModalBackDrop,
    ModalEditMode,
    ModalSize,
    VpJob,
    VpUser,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { EquipmentReportModalComponent } from './equipment-report-modal.component'

@Component({
    selector: 'app-equipment-report',
    templateUrl: './equipment-report.component.html',
    styleUrls: ['./equipment-report.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        GridSelectionCalculationComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class EquipmentReportComponent extends BaseComponent implements OnDestroy {
    private destroyRef = inject(DestroyRef)
    private router = inject(Router)
    grid: Grid<EquipmentReport>
    jobMap = signal<ListItem<string>[]>([])
    employeeMap = signal<ListItem<number>[]>([])
    equipmentMap = signal<ListItem<string>[]>([])
    reportTypeDataMap = EquipmentReportTypeEx.toDataMap()
    employeeDataMap!: DataMap
    jobDataMap!: DataMap
    equipmentDataMap!: DataMap

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsEquipmentReportView])
    }

    constructor() {
        super()
        this.grid = new Grid<EquipmentReport>(this.services)
            .setupModalEditing(
                this.services.data.equipmentReports,
                'Equipment Report',
                EquipmentReportModalComponent,
                () => new EquipmentReport(),
                { component: this },
                ModalSize.large,
            )
            .wrapColumnHeaders()
            .filterBy(item => [item.reportType, item.equipment, item.job])

        this.grid.modified.on(this.refreshItems.bind(this))
        this.loadJobs()

        if (!this.router.url.includes('/list')) {
            this.openWizardModal()
        }
    }

    ngOnDestroy() {
        this.grid.modified.off(this.refreshItems)
    }

    private openWizardModal() {
        const target = new EquipmentReport()
        const modalEditOptions: IModalEditOptions<EquipmentReport> = {
            target,
            initializeParams: { component: this },
            mode: ModalEditMode.adding,
            repository: this.services.data.equipmentReports,
            typeName: 'Equipment Report',
        }

        const ref = this.services.modals.open(EquipmentReportModalComponent, ModalSize.medium, ModalBackDrop.darkAndInactive)
        const repoModal = ref.componentInstance as EquipmentReportModalComponent
        repoModal.initialize(modalEditOptions)

        ref.result.then(
            () => {
                this.refreshItems()
            },
            () => {},
        )
    }

    private loadJobs() {
        this.services.data
            .load(
                false,
                false,
                this.services.data.get<VpJob[]>('api/VpJob'),
                this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                this.services.data.get<any[]>('api/VpEquipment'),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    const jobs = result[0] as VpJob[]
                    this.jobMap.set([
                        new ListItem<string>('', 'Select a Job'),
                        ...jobs
                            .map(job => new ListItem<string>(job.id, job.id + ' ' + job.name))
                            .sort((a, b) => a.display.localeCompare(b.display)),
                    ])
                    this.jobDataMap = new DataMap(
                        jobs.map(j => ({ key: j.id, display: j.id + ' ' + j.name })),
                        'key',
                        'display',
                    )

                    const users = result[1] as VpUser[]
                    this.employeeMap.set([
                        new ListItem<number>(0, 'Select...'),
                        ...users.map(u => new ListItem<number>(u.id, u.name)).sort((a, b) => a.display.localeCompare(b.display)),
                    ])
                    this.employeeDataMap = new DataMap(users, 'id', 'name')

                    const equipment = (result[2] as any[]).orderBy((x: any) => x.equipment)
                    this.equipmentMap.set([
                        new ListItem<string>('', 'Select...'),
                        ...equipment.map(x => new ListItem<string>(x.equipment, x.equipment + ': ' + x.description)),
                    ])
                    this.equipmentDataMap = new DataMap(
                        equipment.map((x: any) => ({ key: x.equipment, display: x.equipment + ': ' + x.description })),
                        'key',
                        'display',
                    )

                    this.refreshItems()
                    this.initialLoadingComplete()
                },
                error: () => {
                    this.initialLoadingComplete()
                },
            })
    }

    refreshItems() {
        this.services.data.equipmentReports
            .queryAll()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.grid.items = result
                },
            })
    }

    refresh() {
        this.refreshItems()
    }
}
